import { useState } from 'react';
import { Link } from 'react-router-dom';
import SiteLayout from '@/components/feature/SiteLayout';
import Breadcrumb from '@/components/base/Breadcrumb';

const SUBMIT_URL = 'https://readdy.ai/api/form/db78ia3askivmpnrchkg';

type Status = 'idle' | 'success' | 'error';

export default function About() {
  const [status, setStatus] = useState<Status>('idle');
  const [feedback, setFeedback] = useState('');

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    // Anti-spam honeypot
    const honeypot = (formData.get('website_alt') as string | null)?.trim() || '';
    if (honeypot) {
      setStatus('success');
      setFeedback('留言已提交，感谢你的反馈！');
      form.reset();
      return;
    }
    formData.delete('website_alt');

    try {
      const response = await fetch(SUBMIT_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string }; message?: string } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '';
      const isSpam = serverMsg.toLowerCase().includes('spam');
      const ok = response.ok && parsed?.code === 'OK' && !isSpam;

      if (ok) {
        setStatus('success');
        setFeedback('留言已提交，感谢你的反馈！我们会尽快查看。');
        form.reset();
      } else {
        setStatus('error');
        setFeedback(serverMsg || '提交失败，请稍后再试。');
      }
    } catch {
      setStatus('error');
      setFeedback('网络异常，提交失败，请稍后再试。');
    }
  };

  return (
    <SiteLayout>
      <title>关于我们 - 蜜桃成熟时33D影视资讯站</title>
      <meta
        name="description"
        content="了解蜜桃成熟时33D影视资讯站的网站定位与内容范围，查看免责声明与版权说明，并通过留言表单与我们取得联系。"
      />
      <meta name="keywords" content="关于我们,网站介绍,联系方式,免责声明,版权声明" />
      <div className="mx-auto w-full px-4 py-10 md:px-6 md:py-14">
        <Breadcrumb items={[{ label: '首页', to: '/' }, { label: '关于我们' }]} />

        {/* Intro */}
        <div className="mt-6 rounded-lg border border-background-200 bg-background-100 p-6 md:p-10">
          <h1 className="font-heading text-2xl font-bold text-foreground-950 md:text-4xl">
            关于本站
          </h1>
          <p className="mt-4 max-w-3xl text-sm leading-7 text-foreground-700">
            蜜桃成熟时33D 影视资讯站是一个专注于影视内容分享的信息型网站，围绕《蜜桃成熟时33D》及其相关系列与同类型作品，为影迷提供影片资料、剧情介绍、演员信息与观影指南，帮助用户更方便地获取详细影视内容。
          </p>
        </div>

        {/* Content scope + Disclaimer */}
        <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-3">
          <div className="rounded-lg border border-background-200 bg-background-50 p-6">
            <span className="flex h-10 w-10 items-center justify-center rounded-md bg-primary-100 text-primary-600">
              <i className="ri-book-2-line text-lg"></i>
            </span>
            <h2 className="mt-4 font-heading text-lg font-bold text-foreground-950">内容范围</h2>
            <ul className="mt-3 space-y-2 text-sm leading-relaxed text-foreground-600">
              <li className="flex gap-2"><i className="ri-check-line mt-1 text-primary-500"></i>影片资料与剧情介绍</li>
              <li className="flex gap-2"><i className="ri-check-line mt-1 text-primary-500"></i>演员信息与代表作品</li>
              <li className="flex gap-2"><i className="ri-check-line mt-1 text-primary-500"></i>同类型影视作品推荐</li>
              <li className="flex gap-2"><i className="ri-check-line mt-1 text-primary-500"></i>观影指南与热门专题</li>
            </ul>
          </div>

          <div className="rounded-lg border border-background-200 bg-background-50 p-6">
            <span className="flex h-10 w-10 items-center justify-center rounded-md bg-accent-100 text-accent-700">
              <i className="ri-shield-line text-lg"></i>
            </span>
            <h2 className="mt-4 font-heading text-lg font-bold text-foreground-950">免责声明</h2>
            <p className="mt-3 text-sm leading-relaxed text-foreground-600">
              本站为纯演示项目，不提供任何真实影片播放、下载或资源分发服务。站内所有影片、演员及相关数据均为虚构演示内容，如与现实作品雷同，纯属巧合。
            </p>
          </div>

          <div className="rounded-lg border border-background-200 bg-background-50 p-6">
            <span className="flex h-10 w-10 items-center justify-center rounded-md bg-secondary-100 text-secondary-900">
              <i className="ri-copyright-line text-lg"></i>
            </span>
            <h2 className="mt-4 font-heading text-lg font-bold text-foreground-950">版权声明</h2>
            <p className="mt-3 text-sm leading-relaxed text-foreground-600">
              本站页面设计、文字内容均为演示用途。如涉及任何第三方权益，请通过下方联系方式与我们取得联系，我们将及时处理。
            </p>
          </div>
        </div>

        {/* Contact form */}
        <div className="mt-10 grid grid-cols-1 gap-8 lg:grid-cols-12">
          <div className="lg:col-span-4">
            <h2 className="font-heading text-2xl font-bold text-foreground-950">联系我们</h2>
            <p className="mt-3 text-sm leading-7 text-foreground-600">
              如果你对本站内容有任何建议或反馈，欢迎通过下面的表单留言，我们会认真阅读每一条意见。
            </p>
            <ul className="mt-6 space-y-4 text-sm text-foreground-700">
              <li className="flex items-center gap-3">
                <span className="flex h-9 w-9 items-center justify-center rounded-md bg-background-100 text-primary-600">
                  <i className="ri-mail-line"></i>
                </span>
                service@peach33d.example.com
              </li>
              <li className="flex items-center gap-3">
                <span className="flex h-9 w-9 items-center justify-center rounded-md bg-background-100 text-primary-600">
                  <i className="ri-chat-3-line"></i>
                </span>
                留言后我们会在工作日内回复
              </li>
              <li className="flex items-center gap-3">
                <span className="flex h-9 w-9 items-center justify-center rounded-md bg-background-100 text-primary-600">
                  <i className="ri-time-line"></i>
                </span>
                内容每日更新
              </li>
            </ul>
          </div>

          <div className="lg:col-span-8">
            <form
              id="contact-feedback-form"
              data-readdy-form
              onSubmit={handleSubmit}
              className="contact-form-wrap rounded-lg border border-background-200 bg-background-50 p-6 md:p-8"
            >
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                <div>
                  <label htmlFor="name" className="mb-1.5 block text-sm font-medium text-foreground-700">
                    你的称呼 <span className="text-primary-500">*</span>
                  </label>
                  <input
                    id="name"
                    name="name"
                    type="text"
                    required
                    placeholder="请输入你的称呼"
                    className="w-full rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-800 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="mb-1.5 block text-sm font-medium text-foreground-700">
                    电子邮箱 <span className="text-primary-500">*</span>
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    required
                    placeholder="用于接收回复"
                    className="w-full rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-800 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
                  />
                </div>
              </div>

              <div className="mt-5">
                <label htmlFor="subject" className="mb-1.5 block text-sm font-medium text-foreground-700">
                  反馈类型
                </label>
                <select
                  id="subject"
                  name="subject"
                  className="w-full rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-800 focus:border-primary-400 focus:outline-none"
                >
                  <option value="内容建议">内容建议</option>
                  <option value="影片资讯补充">影片资讯补充</option>
                  <option value="问题反馈">问题反馈</option>
                  <option value="合作与版权">合作与版权</option>
                </select>
              </div>

              <div className="mt-5">
                <label htmlFor="message" className="mb-1.5 block text-sm font-medium text-foreground-700">
                  留言内容 <span className="text-primary-500">*</span>
                </label>
                <textarea
                  id="message"
                  name="message"
                  required
                  maxLength={500}
                  rows={5}
                  placeholder="请输入你的建议或反馈（最多 500 字）"
                  className="w-full resize-none rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-800 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
                ></textarea>
              </div>

              {/* Anti-spam honeypot */}
              <div className="field-optional-note">
                <label htmlFor="website_alt">公司网站（选填）</label>
                <input
                  className="input-website-alt"
                  id="website_alt"
                  name="website_alt"
                  type="text"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />
              </div>

              <div className="mt-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                <button
                  type="submit"
                  className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
                >
                  <i className="ri-send-plane-line"></i>
                  提交留言
                </button>

                {status !== 'idle' && (
                  <p
                    className={`flex items-center gap-2 text-sm ${
                      status === 'success' ? 'text-primary-600' : 'text-secondary-700'
                    }`}
                  >
                    <i
                      className={
                        status === 'success' ? 'ri-checkbox-circle-line' : 'ri-error-warning-line'
                      }
                    ></i>
                    {feedback}
                  </p>
                )}
              </div>

              <p className="mt-4 text-xs text-foreground-400">
                温馨提示：本站为演示项目，表单提交用于展示交互效果，我们不会将你的信息用于其他用途。
              </p>
            </form>
          </div>
        </div>

        <div className="mt-10 text-center">
          <Link
            to="/movies"
            className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-5 py-2.5 text-sm font-medium text-foreground-800 transition-colors hover:border-primary-400 hover:text-primary-600"
          >
            <i className="ri-compass-3-line"></i>
            去浏览电影分类
          </Link>
        </div>
      </div>
    </SiteLayout>
  );
}